/**
 * 走势图导出为图片(纯 Canvas,不依赖 html2canvas)
 *
 * 用法:
 *   const dataUrl = exportTrendToImage(history, options);
 *   // dataUrl 是 base64 PNG,可以直接 <a> 下载或分享
 */
import { history } from '../data/history.js';

const COLORS = {
  bg: '#0a0a1a',
  card: '#1a1a2e',
  text: '#e0e0e0',
  text2: '#9090a0',
  cyan: '#22d3ee',
  gold: '#fbbf24',
  purple: '#a855f7',
  red: '#ef4444',
  green: '#22c55e',
};

const TIER_COLORS = ['#3b3b5e', '#22d3ee', '#a855f7', '#fbbf24'];

/**
 * 绘制走势图(返回 dataURL)
 * @param {Array} hist - 历史(从老到新)
 * @param {Object} options
 * @param {number} options.width - 画布宽度(默认 750)
 * @param {number} options.periodCount - 显示多少期(默认 30)
 * @param {string} options.brand - 品牌名(默认 "蜜蜂空间")
 * @param {string} options.tagline - 副标题
 */
export function exportTrendToImage(hist, options = {}) {
  const {
    width = 750,
    periodCount = 30,
    brand = '蜜蜂空间',
    tagline = '专业杀号 · 大底池筑底',
  } = options;

  const recent = (hist || history).slice(-periodCount);
  if (recent.length === 0) return null;

  const height = 1100;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // 背景
  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  // 顶部品牌
  const grad = ctx.createLinearGradient(0, 0, width, 0);
  grad.addColorStop(0, COLORS.cyan);
  grad.addColorStop(0.5, COLORS.purple);
  grad.addColorStop(1, COLORS.gold);
  ctx.fillStyle = grad;
  ctx.font = 'bold 36px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(brand, width / 2, 50);

  ctx.fillStyle = COLORS.text2;
  ctx.font = '14px sans-serif';
  ctx.fillText(tagline, width / 2, 75);

  // 装饰线
  ctx.strokeStyle = COLORS.cyan;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(40, 95);
  ctx.lineTo(width - 40, 95);
  ctx.stroke();

  // 1. 最近 N 期开奖
  drawSection(ctx, '🎯 最近 ' + recent.length + ' 期开奖', 115, width);
  const gridY = 145;
  const gridH = 280;
  const cols = 10;
  const cellW = (width - 80) / cols;
  const cellH = gridH / 3;
  const maxIdx = recent.length - 1;
  recent.forEach((h, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = 40 + col * cellW;
    const y = gridY + row * cellH;
    // 圆点
    ctx.fillStyle = COLORS.card;
    ctx.fillRect(x + 2, y, cellW - 4, cellH - 4);
    const cx = x + cellW / 2;
    const cy = y + cellH / 2 - 8;
    const r = Math.min(cellW, cellH) * 0.3;
    // 数字
    const code = `${h.a}${h.b}${h.c}`;
    ctx.font = `bold ${r * 1.0}px monospace`;
    ctx.fillStyle = i === maxIdx ? COLORS.gold : COLORS.text;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(code, cx, cy);
    // 期号
    ctx.fillStyle = COLORS.text2;
    ctx.font = '10px sans-serif';
    const p = String(h.p || h.period || '');
    ctx.fillText(p.slice(-3), cx, cy + r * 0.85);
  });

  // 2. 热号 TOP 5
  const hotY = gridY + gridH + 30;
  drawSection(ctx, '🔥 近 30 期热号 TOP 5', hotY, width);
  const freq = {};
  recent.forEach(h => {
    [h.a, h.b, h.c].forEach(d => { freq[d] = (freq[d] || 0) + 1; });
  });
  const top = Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const hotGridY = hotY + 30;
  const hotW = (width - 80) / 5;
  top.forEach(([d, c], i) => {
    const x = 40 + i * hotW;
    ctx.fillStyle = COLORS.card;
    ctx.fillRect(x + 4, hotGridY, hotW - 8, 80);
    ctx.fillStyle = i === 0 ? COLORS.gold : i < 3 ? COLORS.cyan : COLORS.text2;
    ctx.font = 'bold 32px monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(d), x + hotW / 2, hotGridY + 30);
    ctx.font = '12px sans-serif';
    ctx.fillStyle = COLORS.text2;
    ctx.fillText(`出现 ${c} 次`, x + hotW / 2, hotGridY + 60);
  });

  // 3. 形态分布
  const distY = hotGridY + 110;
  drawSection(ctx, '📊 形态分布', distY, width);
  const stats = { 组六: 0, 组三: 0, 豹子: 0 };
  recent.forEach(h => { stats[h.type] = (stats[h.type] || 0) + 1; });
  const statY = distY + 30;
  const total = recent.length;
  ['组六', '组三', '豹子'].forEach((t, i) => {
    const c = stats[t] || 0;
    const pct = total > 0 ? (c / total * 100).toFixed(0) : 0;
    const x = 40 + i * ((width - 80) / 3);
    const w = (width - 80) / 3 - 8;
    ctx.fillStyle = COLORS.card;
    ctx.fillRect(x, statY, w, 60);
    ctx.fillStyle = [COLORS.green, COLORS.cyan, COLORS.purple][i];
    ctx.font = 'bold 18px sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(t, x + 10, statY + 8);
    ctx.fillStyle = COLORS.text;
    ctx.font = 'bold 22px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`${c}`, x + w - 10, statY + 8);
    ctx.fillStyle = COLORS.text2;
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText(`${pct}%`, x + w - 10, statY + 36);
  });

  // 4. 跨度/和值 曲线
  const curveY = statY + 80;
  drawSection(ctx, '📈 跨度走势', curveY, width);
  drawCurve(ctx, recent.map(h => h.span), 0, 10, curveY + 30, width - 80, 100, COLORS.cyan);

  const sumY = curveY + 150;
  drawSection(ctx, '📈 和值走势', sumY, width);
  drawCurve(ctx, recent.map(h => h.sum), 0, 30, sumY + 30, width - 80, 100, COLORS.gold);

  // 底部品牌
  ctx.fillStyle = COLORS.text2;
  ctx.font = '12px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(`数据来源:历史开奖 · 仅供研究参考,理性投注`, width / 2, height - 30);
  ctx.fillStyle = COLORS.cyan;
  ctx.fillText(`© ${brand} v2.6.0`, width / 2, height - 12);

  return canvas.toDataURL('image/png');
}

function drawSection(ctx, title, y, width) {
  ctx.fillStyle = '#e0e0e0';
  ctx.font = 'bold 16px sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(title, 40, y);
}

function drawCurve(ctx, data, min, max, y, w, h, color) {
  if (data.length === 0) return;
  const range = max - min || 1;
  const step = w / Math.max(1, data.length - 1);
  const points = data.map((v, i) => ({
    x: 40 + i * step,
    y: y + h - ((v - min) / range) * h,
  }));
  // 填充
  const grad = ctx.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, color + '66');
  grad.addColorStop(1, color + '00');
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.moveTo(points[0].x, y + h);
  points.forEach(p => ctx.lineTo(p.x, p.y));
  ctx.lineTo(points[points.length - 1].x, y + h);
  ctx.closePath();
  ctx.fill();
  // 线
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.beginPath();
  points.forEach((p, i) => {
    if (i === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.stroke();
  // 点
  points.forEach(p => {
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
    ctx.fill();
  });
}

/**
 * 下载图片(dataURL → 文件)
 */
export function downloadImage(dataUrl, filename = '3d-max-trend.png') {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * 分享图片(调起系统分享 / 复制到剪贴板)
 */
export async function shareImage(dataUrl, title = '蜜蜂空间 走势图') {
  // 优先使用 navigator.share
  if (navigator.canShare) {
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'trend.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title, text: '蜜蜂空间 走势图' });
        return 'shared';
      }
    } catch {}
  }
  // fallback: 复制 dataURL 到剪贴板(图片)
  try {
    const blob = await (await fetch(dataUrl)).blob();
    if (navigator.clipboard?.write && window.ClipboardItem) {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': blob }),
      ]);
      return 'copied';
    }
  } catch {}
  // 最后: 触发下载
  downloadImage(dataUrl);
  return 'downloaded';
}
